import { REPORT_SCRIPT, REPORT_THEME } from "./report-theme.js";
import { reportBlockIsRaw, reportReadingSections, reportTime } from "./report-reading.js";
export { reportReadingSections, reportBlockIsRaw, reportTime } from "./report-reading.js";
export { REPORT_DEMO_PREVIEW_STYLE } from "./report-theme.js";
export function escapeReportText(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}
const valueText = (value) => typeof value === "number"
    ? new Intl.NumberFormat("zh-CN", { maximumSignificantDigits: 8 }).format(value)
    : value === null
        ? "—"
        : value === true
            ? "是"
            : value === false
                ? "否"
                : String(value);
const palette = [
    "#0f7658",
    "#397da2",
    "#aa7218",
    "#9b507d",
    "#567143",
    "#7a629e",
    "#b65a45",
    "#417b7e",
];
/** 窄屏表格保留完整列，并为键盘与触屏用户提供滚动入口。 */
function scrollableTable(html) {
    return `<div class="table-scroll" role="region" aria-label="表格数据，可左右滚动" tabindex="0">${html}</div><p class="table-hint">表格较宽时，可左右滚动查看所有列。</p>`;
}
/** 仅允许当前报告登记的附件；浏览器预览由宿主通过受控消息打开。 */
function artifactLink(artifact, caption) {
    return `<a href="${escapeReportText(artifact.path)}" data-artifact-id="${escapeReportText(artifact.artifactId)}">${escapeReportText(caption ?? artifact.name)}</a>`;
}
/** 安全的报告 Markdown 子集；原始 HTML 一律转义，外部资源不自动加载。 */
function prose(markdown, artifacts) {
    function inline(line) {
        return escapeReportText(line)
            .replace(/`([^`]+)`/g, "<code>$1</code>")
            .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, text, href) => {
            const artifact = artifacts.find((item) => escapeReportText(item.path) === href || escapeReportText(item.artifactId) === href);
            if (artifact)
                return artifactLink(artifact, text.replaceAll("&amp;", "&").replaceAll("&lt;", "<").replaceAll("&gt;", ">"));
            return /^https:\/\//.test(href)
                ? `<a href="${href}" target="_blank" rel="noreferrer">${text}</a>`
                : text;
        });
    }
    let code = false, codeLines = [], list = false;
    const output = [];
    const lines = markdown.split("\n");
    for (let index = 0; index < lines.length; index++) {
        const line = lines[index];
        if (line.startsWith("```")) {
            if (list) {
                output.push("</ul>");
                list = false;
            }
            if (code) {
                output.push(`<pre><code>${escapeReportText(codeLines.join("\n"))}</code></pre>`);
                codeLines = [];
            }
            code = !code;
            continue;
        }
        if (code) {
            codeLines.push(line);
            continue;
        }
        const labels = markdownCells(line);
        const separator = lines[index + 1] ? markdownCells(lines[index + 1]) : [];
        if (labels.length > 1 &&
            separator.length === labels.length &&
            separator.every((cell) => /^:?-{3,}:?$/.test(cell.trim()))) {
            if (list) {
                output.push("</ul>");
                list = false;
            }
            index++;
            const rows = [];
            while (index + 1 < lines.length) {
                const cells = markdownCells(lines[index + 1]);
                if (cells.length !== labels.length)
                    break;
                rows.push(cells);
                index++;
            }
            output.push(scrollableTable(`<table><thead><tr>${labels.map((cell) => `<th scope="col">${inline(cell)}</th>`).join("")}</tr></thead><tbody>${rows.map((row) => `<tr>${row.map((cell) => `<td>${inline(cell)}</td>`).join("")}</tr>`).join("")}</tbody></table>`));
            continue;
        }
        if (/^\s*(?:[-*]|\d+[.)])\s+/.test(line)) {
            if (!list) {
                output.push("<ul>");
                list = true;
            }
            output.push(`<li>${inline(line.replace(/^\s*(?:[-*]|\d+[.)])\s+/, ""))}</li>`);
            continue;
        }
        if (list) {
            output.push("</ul>");
            list = false;
        }
        const heading = /^(#{1,6})\s+(.+)$/.exec(line);
        if (heading) {
            const level = Math.min(6, heading[1].length + 2);
            output.push(`<h${level}>${inline(heading[2])}</h${level}>`);
        }
        else if (line.trim())
            output.push(`<p>${inline(line)}</p>`);
    }
    if (list)
        output.push("</ul>");
    if (codeLines.length)
        output.push(`<pre><code>${escapeReportText(codeLines.join("\n"))}</code></pre>`);
    return output.join("");
}
/** 只解析表格的列边界；转义管道与行内代码不形成新列。 */
function markdownCells(line) {
    if (!line.includes("|"))
        return [];
    const cells = [];
    let text = "", quoted = false, escaped = false;
    for (const char of line.trim()) {
        if (escaped) {
            text += char;
            escaped = false;
            continue;
        }
        if (char === "\\") {
            escaped = true;
            continue;
        }
        if (char === "`")
            quoted = !quoted;
        if (char === "|" && !quoted) {
            cells.push(text.trim());
            text = "";
        }
        else
            text += char;
    }
    cells.push(text.trim());
    if (cells[0] === "")
        cells.shift();
    if (cells.at(-1) === "")
        cells.pop();
    return cells;
}
function table(labels, rows, interactive = true) {
    return `<div ${interactive ? "data-report-table" : ""}>
    ${interactive ? '<label class="filter">筛选数据 <input type="search" aria-label="筛选数据"></label>' : ""}
    ${scrollableTable(`<table><thead><tr>${labels.map((label) => `<th scope="col">${interactive ? `<button type="button" title="排序">${escapeReportText(label)}</button>` : escapeReportText(label)}</th>`).join("")}</tr></thead>
    <tbody>${rows
        .map((row) => `<tr>${row
        .map((cell) => {
        const text = valueText(cell);
        return `<td${typeof cell === "number" ? ` data-sort-value="${cell}"` : ""}>${text.includes("\n") || text.length > 200 ? `<pre>${escapeReportText(text)}</pre>` : escapeReportText(text)}</td>`;
    })
        .join("")}</tr>`)
        .join("")}</tbody></table>`)}
    ${interactive ? '<div class="pagination"><button data-prev>上一页</button><span data-page></span><button data-next>下一页</button></div>' : ""}</div>`;
}
function chart(block) {
    const points = block.series.flatMap((series) => series.points);
    const min = Math.min(0, ...points.map((point) => point.y)), max = Math.max(0, ...points.map((point) => point.y));
    const scale = Math.max(Math.abs(min), Math.abs(max), 1);
    const normalizedMin = min / scale, range = max / scale - normalizedMin || 1;
    const width = 800, height = 340, left = 80, top = 24, bottom = 270, right = 775;
    const count = Math.max(...block.series.map((series) => series.points.length), 1);
    const numericX = block.chartType !== "bar" && points.every((point) => typeof point.x === "number");
    const xmin = numericX ? Math.min(...points.map((point) => point.x)) : 0;
    const xmax = numericX ? Math.max(...points.map((point) => point.x)) : count - 1;
    const xscale = Math.max(Math.abs(xmin), Math.abs(xmax), 1);
    const x = (value, index) => left +
        ((numericX ? value / xscale - xmin / xscale : index) /
            ((numericX ? xmax / xscale - xmin / xscale : count - 1) || 1)) *
            (right - left);
    const y = (value) => bottom - ((value / scale - normalizedMin) / range) * (bottom - top);
    const step = (right - left) / Math.max(count, 1);
    let marks = "";
    for (let tick = 0; tick <= 4; tick++) {
        const value = (normalizedMin + (range * tick) / 4) * scale;
        marks += `<line x1="${left}" x2="${right}" y1="${y(value)}" y2="${y(value)}" stroke="#dce3df"/><text x="${left - 8}" y="${y(value) + 4}" text-anchor="end">${escapeReportText(valueText(value))}</text>`;
    }
    block.series.forEach((series, seriesIndex) => {
        const color = palette[seriesIndex % palette.length];
        if (block.chartType === "line")
            marks += `<polyline fill="none" stroke="${color}" stroke-width="2" points="${series.points.map((point, index) => `${x(point.x, index)},${y(point.y)}`).join(" ")}"/>`;
        series.points.forEach((point, index) => {
            const px = block.chartType === "bar"
                ? left + step * index + step * 0.1 + ((step * 0.8) / block.series.length) * seriesIndex
                : x(point.x, index);
            const title = `<title>${escapeReportText(`${point.x} · ${series.label}: ${valueText(point.y)} ${series.unit ?? ""}`)}</title>`;
            if (block.chartType === "bar")
                marks += `<rect x="${px}" y="${Math.min(y(point.y), y(0))}" width="${Math.max(0.5, (step * 0.8) / block.series.length)}" height="${Math.abs(y(point.y) - y(0)) || 1}" fill="${color}">${title}</rect>`;
            else
                marks += `<circle cx="${px}" cy="${y(point.y)}" r="3" fill="${color}">${title}</circle>`;
        });
    });
    const labelEvery = Math.max(1, Math.ceil(count / 8));
    block.series[0]?.points.forEach((point, index) => {
        if (index % labelEvery !== 0 && index !== count - 1)
            return;
        const label = String(point.x);
        marks += `<text x="${block.chartType === "bar" ? left + step * (index + 0.5) : x(point.x, index)}" y="290" text-anchor="middle"><title>${escapeReportText(label)}</title>${escapeReportText(label.length > 18 ? label.slice(0, 15) + "…" : label)}</text>`;
    });
    marks += `<text x="${width / 2}" y="325" text-anchor="middle">${escapeReportText(block.xLabel ?? "")}${escapeReportText(block.xUnit ? ` (${block.xUnit})` : "")}</text>`;
    return `<div class="legend">${block.series.map((series, index) => `<span><svg width="12" height="12" aria-hidden="true"><rect width="10" height="10" fill="${palette[index % palette.length]}"/></svg>${escapeReportText(series.label)}${escapeReportText(series.unit ? ` (${series.unit})` : "")}</span>`).join("")}</div>
  <p class="chart-hint">图表可左右滚动；下方数据提供完整标签与数值。</p><div class="chart-scroll"><svg class="chart" viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeReportText(block.title ?? "科研图表")}">${marks}</svg></div>
  <details class="data-toggle"><summary>查看图表数据</summary>${table([block.xLabel ?? "横轴", "指标", "数值", "单位"], block.series.flatMap((series) => series.points.map((point) => [point.x, series.label, point.y, series.unit ?? "未注明"])))}</details>`;
}
function blockHtml(block, document, artifacts) {
    const title = "title" in block && block.title ? `<h3>${escapeReportText(block.title)}</h3>` : "";
    const raw = reportBlockIsRaw(block, document);
    switch (block.kind) {
        case "markdown":
            return prose(block.markdown, artifacts);
        case "text":
            return title + `<pre>${escapeReportText(block.text)}</pre>`;
        case "metrics":
            return (title +
                (raw
                    ? table(["字段", "值", "单位"], block.items.map((item) => [item.label, item.value, item.unit ?? ""]))
                    : `<dl>${block.items.map((item) => `<div><dt>${escapeReportText(item.label)}</dt><dd>${escapeReportText(valueText(item.value))} <small>${escapeReportText(item.unit ?? "")}</small></dd></div>`).join("")}</dl>`));
        case "table":
            return (title +
                table(block.columns.map((column) => column.label + (column.unit ? ` (${column.unit})` : "")), block.rows.map((row) => row.cells)) +
                (block.truncated ? '<p class="warning">此视图包含数据节选，完整数据请查看来源。</p>' : ""));
        case "chart":
            return (title +
                (raw
                    ? table(["横轴", "字段", "值"], block.series.flatMap((series) => series.points.map((point) => [point.x, series.label, point.y])))
                    : chart(block)) +
                (block.truncated ? '<p class="warning">图表为数据节选。</p>' : ""));
        case "matrix": {
            if (raw)
                return (title +
                    table(["行", ...block.columns.map((column) => column.label)], block.rows.map((row) => [row.label, ...row.values])));
            const values = block.rows.flatMap((row) => row.values), min = Math.min(...values), max = Math.max(...values);
            const scale = Math.max(Math.abs(min), Math.abs(max), 1), normalizedMin = min / scale, range = max / scale - normalizedMin || 1;
            return (title +
                scrollableTable(`<table><thead><tr><th>对象</th>${block.columns.map((column) => `<th>${escapeReportText(column.label + (column.unit ? ` (${column.unit})` : ""))}</th>`).join("")}</tr></thead><tbody>${block.rows.map((row) => `<tr><th>${escapeReportText(row.label)}</th>${row.values.map((value, columnIndex) => `<td class="heat-${Math.round(((value / scale - normalizedMin) / range) * 10)}"><button type="button" data-column-index="${columnIndex}" aria-label="${escapeReportText(`选择 ${row.label}、${block.columns[columnIndex]?.label}: ${valueText(value)}`)}">${escapeReportText(valueText(value))}</button></td>`).join("")}</tr>`).join("")}</tbody></table>`) +
                (block.truncated ? '<p class="warning">矩阵为数据节选。</p>' : ""));
        }
        case "sequence":
            return (title +
                block.items
                    .map((item, index) => `<details><summary>${escapeReportText(item.label)} · ${item.totalLength} 残基</summary><pre>${escapeReportText(item.sequence)}</pre><button type="button" data-sequence-index="${index}">选择序列</button>${item.truncated ? '<p class="warning">序列显示为节选。</p>' : ""}</details>`)
                    .join(""));
        case "artifacts":
            return (title +
                `<ul>${block.items.map((artifact) => `<li>${artifactLink(artifact)} <small>${artifact.sizeBytes} bytes</small></li>`).join("")}</ul>`);
        case "timeline":
            return ("<h3>运行记录</h3>" +
                table(["完成时间", "状态", "溯源标识"], block.items.map((item) => [
                    reportTime(item.completedAt),
                    statusLabel(item.status),
                    item.observationId,
                ])));
        case "notice":
            return `<p class="${block.level === "warning" ? "warning" : "note"}">${escapeReportText(block.message)}</p>`;
        case "provenance":
            return ('<details class="report-provenance"><summary>查看计算溯源</summary>' +
                table(["记录", "状态", "结果摘要"], block.sources.map((source) => [
                    source.observationId,
                    statusLabel(source.status),
                    source.resultDigest,
                ])) +
                "</details>");
    }
}
const statusLabel = (status) => ({ succeeded: "已完成", failed: "失败", cancelled: "已取消", timed_out: "超时" })[status] ?? status;
/** HTML 内容不包含审核状态本身的变化，以便审核摘要绑定稳定的页面。 */
export function renderReportHtml(document) {
    const sections = reportReadingSections(document);
    const artifacts = document.blocks.flatMap((block) => block.kind === "artifacts" ? block.items : []);
    const meta = document.report;
    const date = meta
        ? `生成于 ${reportTime(meta.generatedAt)}${meta.updatedAt !== meta.generatedAt ? ` · 更新于 ${reportTime(meta.updatedAt)}` : ""}`
        : "历史报告 · 原记录未提供生成日期";
    const body = sections
        .map((section) => {
        const tag = section.role === "finding" ? "section" : "details";
        const heading = tag === "details"
            ? `<summary>${escapeReportText(section.title)}</summary>`
            : `<h2>${escapeReportText(section.title)}</h2>`;
        return `<${tag} class="report-section" id="${escapeReportText(section.sectionId)}" data-section-id="${escapeReportText(section.sectionId)}">${heading}<div class="report-layout ${section.layout}">${section.blockIndices
            .map((index, position) => {
            const block = document.blocks[index];
            if (!block)
                return "";
            const nodeTitle = "title" in block ? (block.title ?? `视图 ${position + 1}`) : `说明 ${position + 1}`;
            return `<figure class="report-node" data-block-index="${index}" data-title="${escapeReportText(nodeTitle)}">${blockHtml(block, document, artifacts)}${section.captions[position] ? `<figcaption>${escapeReportText(section.captions[position])}</figcaption>` : ""}</figure>`;
        })
            .join("")}</div></${tag}>`;
    })
        .join("");
    // SVG 平铺覆盖整份正文；不会截获交互，也不依赖打印背景设置。
    const watermark = '<div class="report-watermark" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg"><defs><pattern id="report-watermark-pattern" width="400" height="180" patternUnits="userSpaceOnUse"><text x="44" y="100" transform="rotate(-18 160 80)">RNAVoyager</text></pattern></defs><rect width="100%" height="100%" fill="url(#report-watermark-pattern)"/></svg></div>';
    return `<!doctype html><html lang="zh-CN"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src 'none'; connect-src 'none'; base-uri 'none'; form-action 'none'"><title>${escapeReportText(document.title)} · RNAVoyager</title><style>${REPORT_THEME}</style></head><body>${watermark}<main><header><div class="brand">RNAVoyager</div><h1>${escapeReportText(document.title)}</h1><p class="meta">${escapeReportText(date)}</p></header><nav aria-label="报告目录">${sections.map((section) => `<a href="#${escapeReportText(section.sectionId)}">${escapeReportText(section.title)}</a>`).join("")}</nav>${body}<footer>RNAVoyager · 计算结果与预测不等同于实验验证。大型结构等附件通过链接访问，未嵌入此文件。</footer></main><script>${REPORT_SCRIPT}</script></body></html>`;
}
//# sourceMappingURL=report-html.js.map